import { useEffect, useId, useRef, type ReactNode } from "react"
import { Icon } from "./Icon"

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: "small" | "medium" | "large" | "wide"
  /** Rendered in the header, before the close button (e.g. a "Drafts" action). */
  headerAction?: ReactNode
  /** Set to false for dialogs whose only close affordance is an explicit action. */
  showClose?: boolean
  className?: string
}

/**
 * Accessible dialog: escape closes, background scroll is locked, focus moves
 * into the panel and returns to the trigger when it closes.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "medium",
  headerAction,
  showClose = true,
  className = "",
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement as HTMLElement | null
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    const timer = window.setTimeout(() => {
      const focusable = panelRef.current?.querySelector<HTMLElement>(
        "input, textarea, button, select, [href], [tabindex]:not([tabindex='-1'])",
      )
      focusable?.focus()
    }, 20)

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== "Tab" || !panelRef.current) return
      const focusables = [...panelRef.current.querySelectorAll<HTMLElement>(
        "input, textarea, button, select, [href], [tabindex]:not([tabindex='-1'])",
      )].filter((element) => !element.hasAttribute("disabled"))
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener("keydown", onKeyDown)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener("keydown", onKeyDown)
      document.body.style.overflow = originalOverflow
      previouslyFocused.current?.focus()
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="modal-overlay" data-no-note-nav onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        className={`modal modal-${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
      >
        <header className="modal-header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p className="modal-description">{description}</p>}
          </div>
          <div className="modal-header-actions">
            {headerAction}
            {showClose && (
              <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
                <Icon name="x" />
              </button>
            )}
          </div>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>
  )
}
